import { useState } from "react";
import { Eye, EyeOff, Lock, User } from "lucide-react";
import { useAuth } from "./AuthContext";
import { Button, ErrorNote, Field, inputClass } from "../components/ui";

const LOGO = "https://storage.googleapis.com/lsp-webstore/Dashboard/redlogo.jpeg";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#111111] text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full border-[48px] border-[#C00000]/90" />
        <div className="absolute -bottom-10 -left-4 w-[240px] h-[240px] rounded-full border-[24px] border-[#8B0000]/70" />
        <div className="relative">
          <div className="inline-block bg-white rounded-lg px-4 py-3">
            <img src={LOGO} alt="Lightspeed Photonics" className="h-10 object-contain" />
          </div>
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold leading-tight">Employee Portal</h2>
          <p className="mt-3 text-white/60 leading-relaxed">
            Internal tools, resources and company updates for the Lightspeed Photonics team.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="lg:hidden mb-2">
            <img src={LOGO} alt="Lightspeed Photonics" className="h-12 object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#111111]">Sign in</h1>
            <p className="mt-1 text-sm text-gray-500">
              Use the username and password shared by your administrator.
            </p>
          </div>

          <ErrorNote>{error}</ErrorNote>

          <Field label="Username">
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className={`${inputClass} pl-9`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
          </Field>

          <Field label="Password">
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className={`${inputClass} pl-9 pr-10`}
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>

          <Button type="submit" loading={busy} className="w-full py-2.5">
            Sign in
          </Button>

          <p className="text-xs text-gray-400 text-center">
            You'll stay signed in on this device. Forgot your password? Contact your administrator.
          </p>
        </form>
      </div>
    </div>
  );
}
